import React, { useEffect, useState } from 'react';
import CustomTh from '../../../../../components/table/CustomTh';
import CustomTd from '../../../../../components/table/CustomTd';
import TableFunction from '../../../../../components/table/TableFunction';
import { useTranslation } from 'react-i18next';
import { DEVICE_TYPE, SIOS_TYPE } from '../../SIOs/Model/Models';
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { GetHidRecordDetails } from "../../../../../reduxToolkit/HID/HidApi";
import { TABLES } from "../../../../../Apis/Tables";
import {errorMsg, getValueByKey} from "../../../../../Apis/functions";
import { toast } from "react-toastify";
import {DeleteItemsApi} from "../../../../../reduxToolkit/Commons/CommonsApi";

/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * SiosTableApp displays and manages the SIOs table for a specific controller.
 * Supports selection, deletion, and modal creation for new SIOs.
 *
 * @param {Array} defaultSioList - Optional default list of SIOs to initialize the table.
 *
 * @returns {Object} - Table headers, body, selection state, modal controls, and CRUD functions.
 */
const SiosTableApp = ({ defaultSioList }) => {
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useDispatch();

    const [tableId, setTableId] = useState("tbl_stc_table");
    const [selectedList, setSelectedList] = useState([]);
    const [showCreateSioModal, setShowCreateSioModal] = useState(false);
    const [showDeleteSioModal, setShowDeleteSioModal] = useState(false);
    const [modified, setModified] = useState(false);
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [isAllChecked, setIsAllChecked] = useState(false);

    /**
     * Initialize table with default SIO list if provided
     */
    useEffect(() => {
        if (defaultSioList?.length > 0) {
            setResults({ content: defaultSioList, totalElements: defaultSioList.length });
        }
    }, [defaultSioList]);

    /**
     * Builds table headers
     */
    const getThead = () => [
        <CustomTh tableId={tableId} className='first_head'>{t('name')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('sio_type')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('port_number')}</CustomTh>,
        <CustomTh tableId={tableId}>{t('device_type')}</CustomTh>
    ];

    /**
     * Builds table body
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return { body: [], data: [] };

        const body = results.content.map((item) => (
            <>
                <CustomTd tableId={tableId} className='first-column'>{item?.name}</CustomTd>
                <CustomTd tableId={tableId}>{t(getValueByKey(item?.noSio, 'name', SIOS_TYPE))}</CustomTd>
                <CustomTd tableId={tableId}>{item?.portNumber}</CustomTd>
                <CustomTd tableId={tableId}>{getValueByKey(item?.deviceType, 'name', DEVICE_TYPE)}</CustomTd>
            </>
        ));

        return { body, data: results.content };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        dispatch(GetHidRecordDetails({ id })).then(({ payload: { data: { data, success } } }) => {
            if (success) {
                setResults({ content: data.sios, totalElements: data.sios.length });
            }
        });
    }

    const { thead, tbody, handleCheckboxChange, handleDeleteAll } = TableFunction({
        selectedList,
        setSelectedList,
        data: results,
        getTbody,
        getThead,
        setIsAllChecked,

        getData,//New
        modified,//New
        setModified,//New
    });

    /**
     * Delete selected SIOs
     */
    const deleteSelectedSio = () => {
        const body = selectedList;
        if (body.length > 0) {
            dispatch(DeleteItemsApi({ tableName: TABLES.HID_SIO, body })).then(({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    setSelectedList([]);
                    setIsAllChecked(false);
                    setModified(true);
                }else errorMsg(code);
            });
        }
    };

    return {
        t,
        id,
        thead,
        tbody,
        tableId,
        selectedList,
        showCreateSioModal,
        setShowCreateSioModal,
        showDeleteSioModal,
        setShowDeleteSioModal,
        isAllChecked,
        setModified,
        handleCheckboxChange,
        handleDeleteAll,
        deleteSelectedSio
    };
};

export default SiosTableApp;
